/* global Cart, PubSub */

// Quick add on product cards, mirroring theme-heimatgut: a product with one pack
// adds straight to the cart, a product with several packs opens the shared pack
// dialog. The cart drawer refreshes in the background but stays closed, so the
// customer can move straight on to the next snack.
const ProductCardAdd = (function ProductCardAddModule() {
   const SUCCESS_MS = 1600;
   const REQUEST_TIMEOUT_MS = 15000;

   // Serialize adds so cart refreshes land in the same order as the adds.
   let pendingAdds = Promise.resolve();
   let nextStatusId = 0;
   const successTimers = new WeakMap();

   let dialog = null;
   let dialogContent = null;
   let dialogSubmit = null;
   let dialogSubmitLabel = null;
   let dialogArrow = null;
   let dialogSpinner = null;
   let dialogError = null;
   let dialogCartLink = null;
   let dialogOwner = null;
   let dialogSession = 0;
   let dialogPending = false;

   function getButton(card) {
      return card.querySelector("[data-card-add-button]");
   }

   /* eslint-disable no-param-reassign */
   function setupCard(card) {
      if (card.dataset.cardAddReady === "true") return;
      card.dataset.cardAddReady = "true";

      // A product can appear twice on one page, so status ids are assigned here.
      nextStatusId += 1;
      const status = card.querySelector("[data-card-add-status]");
      status.id = `CardAddStatus-${nextStatusId}`;
      const button = getButton(card);
      button.setAttribute("aria-describedby", status.id);
      button.dataset.addLabel = button.getAttribute("aria-label");
   }

   function updateCount(card, quantity) {
      const counter = card.querySelector("[data-card-add-count]");
      const button = getButton(card);
      counter.textContent = quantity;
      counter.hidden = quantity < 1;
      const countText = quantity
         ? `. ${card.dataset.countLabel.replace("__COUNT__", quantity)}`
         : "";
      button.setAttribute("aria-label", `${button.dataset.addLabel}${countText}`);
   }

   function setCardState(card, state, message) {
      card.dataset.state = state;
      if (message !== undefined)
         card.querySelector("[data-card-add-message]").textContent = message;
   }

   function showCardCartLink(card, visible) {
      card.querySelector("[data-card-add-cart]").hidden = !visible;
   }

   function setCardBusy(card, busy) {
      const button = getButton(card);
      card.dataset.busy = String(busy);
      if (busy) {
         button.setAttribute("aria-busy", "true");
         button.setAttribute("aria-disabled", "true");
      } else {
         button.removeAttribute("aria-busy");
         button.removeAttribute("aria-disabled");
      }
   }
   /* eslint-enable no-param-reassign */

   function setupCards() {
      document.querySelectorAll("[data-card-add]").forEach(setupCard);
   }

   async function syncCounts() {
      try {
         const cart = await Cart.getCart();
         const counts = new Map();
         cart.items.forEach(({ variant_id: variantId, quantity }) => {
            const id = String(variantId);
            counts.set(id, (counts.get(id) ?? 0) + quantity);
         });
         document.querySelectorAll("[data-card-add]").forEach((card) => {
            setupCard(card);
            const quantity = card.dataset.variants
               .split(",")
               .reduce((total, id) => total + (counts.get(id) ?? 0), 0);
            updateCount(card, quantity);
         });
      } catch (error) {
         // eslint-disable-next-line no-console
         console.error("Could not sync card cart counts:", error);
      }
   }

   function getSelectedPack() {
      return dialogContent.querySelector("input[name=pack]:checked");
   }

   function setDialogPending(pending) {
      dialogPending = pending;
      dialogSubmit.disabled = pending || !getSelectedPack();
      dialogSubmit.setAttribute("aria-busy", String(pending));
      dialogContent.querySelectorAll("fieldset").forEach((fieldset) => {
         // eslint-disable-next-line no-param-reassign
         fieldset.disabled = pending;
      });
      dialogSpinner.hidden = !pending;
      dialogArrow.hidden = pending;
      dialogSubmitLabel.textContent = pending
         ? dialogSubmit.dataset.loadingLabel
         : dialogSubmit.dataset.label;
      if (pending) {
         dialogError.hidden = true;
         dialogCartLink.hidden = true;
      }
   }

   function updateSelection() {
      const selected = getSelectedPack();
      dialogError.hidden = true;
      dialogError.textContent = "";
      dialogCartLink.hidden = true;
      dialogSubmit.disabled = dialogPending || !selected;

      const image = dialogContent.querySelector("img");
      if (image && selected?.dataset.image) {
         image.removeAttribute("srcset");
         image.src = selected.dataset.image;
      }
   }

   function closeDialog() {
      if (!dialog?.open) return;
      const owner = dialogOwner;
      dialogOwner = null;
      dialogSession += 1;
      dialog.close();
      document.body.style.overflow = "";
      if (owner?.isConnected) getButton(owner).focus({ preventScroll: true });
   }

   function openDialog(card) {
      const template = card.querySelector("[data-pack-template]");
      if (!dialog || !template) {
         window.location.assign(card.dataset.url);
         return;
      }
      if (dialog.open) return;

      dialogOwner = card;
      dialogSession += 1;
      dialogContent.replaceChildren(template.content.cloneNode(true));
      setDialogPending(false);
      updateSelection();
      dialog.showModal();
      document.body.style.overflow = "hidden";
      const focusTarget = getSelectedPack() ?? dialog.querySelector("[data-pack-close]");
      focusTarget.focus({ preventScroll: true });
   }

   function dialogFail(card, session, message, checkCart) {
      if (dialogOwner !== card || dialogSession !== session || !dialog?.open) return;
      setDialogPending(false);
      updateSelection();
      dialogError.textContent = message;
      dialogError.hidden = false;
      dialogCartLink.hidden = !checkCart;
   }

   function showError(card, message, checkCart, session) {
      dialogFail(card, session, message, checkCart);
      setCardState(card, "error", message);
      showCardCartLink(card, checkCart);
   }

   async function add(card, variantId, session) {
      let added = false;
      try {
         const response = await fetch("/cart/add.js", {
            method: "POST",
            headers: { "Content-Type": "application/json", Accept: "application/json" },
            body: JSON.stringify({ items: [{ id: Number(variantId), quantity: 1 }] }),
            signal: AbortSignal.timeout(REQUEST_TIMEOUT_MS),
         });
         const result = await response.json();
         if (!response.ok || result.status) {
            const uncertain = response.status >= 500;
            const message = uncertain
               ? card.dataset.uncertain
               : (result.description ?? card.dataset.error);
            showError(card, message, uncertain, session);
            return;
         }

         added = true;
         setCardState(card, "success", card.dataset.added);
         if (dialogOwner === card && dialogSession === session) closeDialog();
         successTimers.set(
            card,
            setTimeout(() => {
               if (card.dataset.state === "success") setCardState(card, "idle");
            }, SUCCESS_MS)
         );

         // A drawer refresh failure must never turn a successful add into a retry
         // prompt, which could add the pack twice. Offer the cart link instead.
         try {
            await Cart.refresh();
         } catch (error) {
            // eslint-disable-next-line no-console
            console.error("Card added; cart display could not refresh", error);
            showCardCartLink(card, true);
         }
      } catch {
         if (!added) showError(card, card.dataset.uncertain, true, session);
      } finally {
         setCardBusy(card, false);
      }
   }

   function queueAdd(card, variantId, session) {
      if (card.dataset.busy === "true") return;
      setupCard(card);
      setCardBusy(card, true);
      clearTimeout(successTimers.get(card));
      setCardState(card, "loading", "");
      showCardCartLink(card, false);
      pendingAdds = pendingAdds.catch(() => {}).then(() => add(card, variantId, session));
   }

   function handleCardClick(event) {
      const button = event.target.closest("[data-card-add-button]");
      if (!button) return;
      event.preventDefault();
      const card = button.closest("[data-card-add]");
      if (!card || card.dataset.busy === "true") return;
      setupCard(card);

      if (button.hasAttribute("aria-haspopup")) {
         openDialog(card);
      } else {
         queueAdd(card, card.dataset.variant);
      }
   }

   function bindDialog() {
      dialog = document.querySelector("[data-pack-dialog]");
      if (!dialog) return;

      dialogContent = dialog.querySelector("[data-pack-content]");
      dialogSubmit = dialog.querySelector("[data-pack-submit]");
      dialogSubmitLabel = dialog.querySelector("[data-pack-submit-label]");
      dialogArrow = dialog.querySelector("[data-pack-arrow]");
      dialogSpinner = dialog.querySelector("[data-pack-spinner]");
      dialogError = dialog.querySelector("[data-pack-error]");
      dialogCartLink = dialog.querySelector("[data-pack-cart]");

      dialog.querySelector("[data-pack-close]").addEventListener("click", closeDialog);
      dialog.addEventListener("cancel", (event) => {
         event.preventDefault();
         closeDialog();
      });
      // The form fills the dialog box, so a click that lands on the dialog itself
      // hit its padding or the backdrop.
      dialog.addEventListener("click", (event) => {
         if (event.target === dialog) closeDialog();
      });
      dialogContent.addEventListener("change", updateSelection);
      dialog.querySelector("[data-pack-form]").addEventListener("submit", (event) => {
         event.preventDefault();
         const selected = getSelectedPack();
         if (!selected || dialogPending || dialogOwner?.dataset.busy === "true") return;
         setDialogPending(true);
         queueAdd(dialogOwner, selected.value, dialogSession);
      });
   }

   function init() {
      setupCards();
      bindDialog();
      document.addEventListener("click", handleCardClick);
      // Drawer edits (remove, +/-) change what the card counters should show.
      PubSub.subscribe("cart:updated", syncCounts);
      // Pages restored from the back/forward cache still show the old counts.
      window.addEventListener("pageshow", (event) => {
         if (event.persisted) syncCounts();
      });
   }

   return {
      init,
   };
})();

document.addEventListener("DOMContentLoaded", ProductCardAdd.init);
